/**
 * FP-011 (2026-09-09 audit remediation) — Reset Password page.
 *
 * The backend has generated `/reset-password?token=<x>` links (issued via
 * self-service forgot-password and platform-admin issued resets) and, once
 * FP-010 is fixed, actually emails them — but nothing on the frontend has
 * ever rendered this route. Every reset link a user received led to a
 * dead page. Modeled directly on AcceptInvite.jsx's public, no-auth,
 * no-Shell pattern, calling the existing public
 * `POST /auth/password-reset/consume` endpoint.
 */
import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import LegalFooter from "@/components/legal/LegalFooter";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitError(null);
    if (pwd.length < 8) {
      setSubmitError("Password must be at least 8 characters");
      return;
    }
    if (pwd !== confirm) {
      setSubmitError("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/consume", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 1400);
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <Shell>
        <div data-testid="reset-password-missing-token" className="text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <div className="text-base font-medium text-[var(--fop-text)]">Reset link unavailable</div>
          <div className="text-sm text-[var(--fop-text-2)] mt-1">
            This link is missing its reset token. Request a new one from the sign-in page.
          </div>
          <Link to="/login" data-testid="reset-password-back-to-login" className="inline-block mt-4 px-4 py-2 bg-[var(--fop-primary)] text-black rounded text-sm font-mono uppercase tracking-wider hover:opacity-90">
            Back to sign in
          </Link>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div data-testid="reset-password-done" className="text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <div className="text-base font-medium text-[var(--fop-text)]">Password updated</div>
          <div className="text-sm text-[var(--fop-text-2)] mt-1">
            All other sessions have been signed out for security. Taking you to sign in…
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="text-xs uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1">
        ForgedOps Plans
      </div>
      <h1 className="text-2xl font-semibold text-[var(--fop-text)] mb-1">Reset your password</h1>
      <div className="text-sm text-[var(--fop-text-2)] mb-6">
        Choose a new password for your account.
      </div>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
        <div>
          <Label htmlFor="rp-pwd" className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">New password</Label>
          <Input id="rp-pwd" type="password" value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  data-testid="reset-password-input"
                  className="mt-1 bg-[var(--fop-surface-2)] border-[var(--fop-border)]" />
        </div>
        <div>
          <Label htmlFor="rp-conf" className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Confirm password</Label>
          <Input id="rp-conf" type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-password-confirm"
                  className="mt-1 bg-[var(--fop-surface-2)] border-[var(--fop-border)]" />
        </div>
        {submitError && (
          <div data-testid="reset-password-error" className="text-xs text-red-400">{submitError}</div>
        )}
        <Button type="submit" disabled={submitting}
                 data-testid="reset-password-submit"
                 className="w-full bg-[var(--fop-primary)] hover:opacity-90">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
        </Button>
      </form>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[var(--fop-bg)] flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm rounded-xl border border-[var(--fop-border)] bg-[var(--fop-surface)] p-8">
          {children}
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}
